// MyRecurring — donor-portal "my recurring subscriptions" page (β.2 of the
// donor-portal program; CHG-1496 / ITER-3614). Route: /me/recurring.
//
// Consumes GET /api/v1/me/recurring-donations (CHG-1485 / α.3, hand-rolled
// donor-scope sibling of the admin recurring-donations endpoint). Donor sees
// every recurring subscription they own across every org their account is
// linked to (cross-org by default; not org-scoped).
//
// UI: two TabNav tabs — Active + Cancelled. Each tab mounts its own
// <ListPage> instance with a distinct recurring_status filter. Tab state
// lives in `?tab=active|cancelled` so refresh + deep-link preserve it
// (per TabNav contract). Columns per the program brief: next_charge_at,
// amount, designation, cadence.
//
// Donor scoping: the endpoint resolves donor_ids from the authenticated
// account server-side; the FE never sends a donor_id. Auth is handled by
// <ProtectedRoute> (no requirePermission — donors lack admin permissions;
// gating is on authenticated session only).
//
// Tab-set scope (active vs cancelled) is the program brief's literal split.
// Paused / past_due / pending / suspended states are not surfaced here yet;
// a future iteration can widen the tab set or fold them in if donor UX
// research shows demand.
//
// Hand-rolled exception per feedback_deterministic_codegen_over_hand_rolling.md
// — the v2 page generator does not emit /me/* pages yet (matches the precedent
// set by α-phase me_*.py routers); generator wiring is a γ-phase follow-up
// per the donor-portal program plan.

import { useMemo, useState } from "react";
import { ListPage } from "../components/composition/ListPage";
import type { ListPageConfig } from "../components/composition/ListPage";
import { TabNav } from "../components/composition/TabNav";
import { meRecurringApi } from "../api/me-recurring";
import type { PortalRecurringDonationRead } from "../api/me-recurring";

const TABS = [
  { key: "active", label: "Active" },
  { key: "cancelled", label: "Cancelled" },
];

// BE enum value for the "cancelled" UX label — RecurringStatus uses the
// US spelling "canceled" (single L). Map at the FE/BE boundary.
const STATUS_BY_TAB: Record<string, string> = {
  active: "active",
  cancelled: "canceled",
};

const FREQUENCY_LABEL: Record<string, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

function formatAmount(row: PortalRecurringDonationRead): string {
  // amount_minor_units → display string. Currency defaults to USD if absent
  // (donor.entity-hint surfaces currency per row; legacy rows can carry
  // null). The Intl.NumberFormat call is small enough to inline; if a
  // future i18n CHG centralizes formatters we'll route through that.
  const cents = row.amount_minor_units;
  const currency = row.currency || "USD";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    // Bad currency code → render plain "12.34 XYZ" rather than throw.
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function formatNextCharge(row: PortalRecurringDonationRead): string {
  if (!row.next_charge_at) return "—";
  const d = new Date(row.next_charge_at);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDesignation(row: PortalRecurringDonationRead): string {
  return row.designation_name || row.designation_id?.slice(0, 8) + "…" || "—";
}

function formatCadence(row: PortalRecurringDonationRead): string {
  return FREQUENCY_LABEL[row.frequency] ?? row.frequency ?? "—";
}

function buildConfig(tabKey: string): ListPageConfig<PortalRecurringDonationRead> {
  const status = STATUS_BY_TAB[tabKey];
  return {
    title: tabKey === "active" ? "Active recurring gifts" : "Cancelled recurring gifts",
    subtitle:
      tabKey === "active"
        ? "Your recurring donations that are currently charging."
        : "Recurring donations you have cancelled. Historical record only — they no longer charge.",
    queryKey: ["me-recurring-donations", "list", tabKey] as const,
    queryFn: ({ page, pageSize, search }) =>
      meRecurringApi.listMyRecurringDonations({
        page,
        pageSize,
        search,
        recurring_status: status,
      }),
    pageSize: 25,
    search: { enabled: false }, // donor probably has <50 subscriptions; search not yet warranted
    emptyState: {
      title:
        tabKey === "active"
          ? "You don't have any active recurring donations"
          : "You haven't cancelled any recurring donations",
      subtitle:
        tabKey === "active"
          ? "Set up a recurring gift from any organization's donation page to see it here."
          : "Cancelled subscriptions appear here as a record once you stop them.",
    },
    columns: [
      {
        field: "next_charge_at",
        label: "Next charge",
        type: "custom",
        render: (row) => formatNextCharge(row),
      },
      {
        field: "amount_minor_units",
        label: "Amount",
        type: "custom",
        render: (row) => formatAmount(row),
      },
      {
        field: "designation_name",
        label: "Designation",
        type: "custom",
        render: (row) => formatDesignation(row),
      },
      {
        field: "frequency",
        label: "Cadence",
        type: "custom",
        render: (row) => formatCadence(row),
      },
    ],
  };
}

export function MyRecurring() {
  const [activeTab, setActiveTab] = useState<string>("active");
  const config = useMemo(() => buildConfig(activeTab), [activeTab]);
  return (
    <main style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px 0" }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 4px" }}>My recurring gifts</h1>
      <p style={{ color: "var(--color-text-muted)", margin: "0 0 16px" }}>
        Manage and review every recurring donation you&rsquo;ve set up across the organizations you support.
      </p>
      <TabNav tabs={TABS} defaultTab="active" onActiveChange={setActiveTab} />
      {/* ListPage owns its own pagination + state; mounting two distinct
          instances (one per tab) gives natural state isolation, fresh
          react-query cache key per tab, and zero risk of cross-tab data
          leakage. The `key={activeTab}` forces full remount on tab switch,
          which resets pagination to page 0 cleanly. */}
      <ListPage key={activeTab} config={config} />
    </main>
  );
}
